import React, {useCallback, useMemo, useState} from 'react';
import {DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import Dialog from '../../../components/base/dialog';
import Button from '../../../components/base/button/button';
import {useTranslation} from 'react-i18next';
import SuggestiveSellingDialogContent, {buildStagedLinesFromState} from './suggestive-selling-dialog-content';
import {
    createEmptyStagingState,
    getStagingSummary,
    resolveDefaultSourceQuantity,
} from '../utils/suggestive-selling-staging';

const SuggestiveSellingDialog = ({
                                     open,
                                     onClose,
                                     committing,
                                     crossSales,
                                     sourcePartNumber,
                                     sourceDefaultQuantity,
                                     chainDepth,
                                     onCommit,
                                     disableActions,
                                 }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.suggestive-selling'});
    const [staging, setStaging] = useState(createEmptyStagingState);

    const subtitle = useMemo(() => {
        if (chainDepth > 0) {
            return t('becauseYouAdded', {partNumber: sourcePartNumber});
        }
        return t('subtitle', {partNumber: sourcePartNumber});
    }, [chainDepth, sourcePartNumber, t]);

    const stagedLineCount = useMemo(
        () => getStagingSummary(staging).lineCount,
        [staging],
    );

    const handleCommit = useCallback(() => {
        const lines = buildStagedLinesFromState(crossSales, staging);
        void onCommit(lines);
    }, [crossSales, onCommit, staging]);

    const doneLabel = useMemo(() => {
        if (stagedLineCount > 0) {
            return t('doneAddCount', {count: stagedLineCount});
        }
        return chainDepth > 0 ? t('skip') : t('done');
    }, [chainDepth, stagedLineCount, t]);

    const defaultQuantity = resolveDefaultSourceQuantity(sourceDefaultQuantity);

    return (
        <Dialog
            open={open}
            setOpen={(value) => {
                if (!value) {
                    onClose();
                }
            }}
            maxWidth={'lg'}
            fullWidth
            className={'suggestive-selling-dialog'}
            disableBackdropClick={committing}
        >
            <DialogTitle>
                <Typography variant={'h6'} component={'p'}>
                    {t('title')}
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 0.5}}>
                    {subtitle}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <SuggestiveSellingDialogContent
                    crossSales={crossSales}
                    sourceDefaultQuantity={defaultQuantity}
                    disabled={disableActions || committing}
                    onStagingChange={setStaging}
                />
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0}}>
                <Button
                    variant={'dialog'}
                    color={'primary'}
                    borderRadius={'8px 0 8px 0'}
                    onClick={stagedLineCount > 0 ? handleCommit : onClose}
                    disabled={committing}
                >
                    {committing ? t('committing') : doneLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SuggestiveSellingDialog;
